"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Home, LayoutDashboard, PiggyBank, TrendingUp, Receipt, Target, BookOpen, Settings, Menu, X, Table2 } from "lucide-react";
import { UserButton } from "@clerk/nextjs";
import Image from "next/image";
import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const navItems = [
  { href: "/", label: "Home", icon: Home },
  { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { href: "/savings", label: "Savings", icon: PiggyBank },
  { href: "/investments", label: "Investments", icon: TrendingUp },
  { href: "/expenses", label: "Expenses", icon: Receipt },
  { href: "/goals", label: "Goals", icon: Target },
  { href: "/literacy", label: "Financial Literacy", icon: BookOpen },
  { href: "/tables", label: "Tables", icon: Table2 },
  { href: "/settings", label: "Settings", icon: Settings },
];

export function AppSidebar() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="outline"
        size="icon"
        className="fixed left-4 top-3 z-50 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white lg:hidden"
        aria-label="Toggle navigation"
        onClick={() => setOpen((value) => !value)}
      >
        {open ? <X className="size-4" /> : <Menu className="size-4" />}
      </Button>

      {open ? <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={() => setOpen(false)} aria-hidden /> : null}

      <aside
        className={cn(
          "fixed left-0 top-0 z-40 flex h-full w-64 flex-col border-r border-sidebar-border bg-sidebar transition-transform duration-300 ease-in-out lg:static lg:h-screen",
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        <div className="border-b border-sidebar-border px-5 py-5">
          <Link href="/" onClick={() => setOpen(false)} className="block">
            <Image
              src="/softwareLogo.png"
              alt="Budget Track"
              width={220}
              height={70}
              className="h-auto w-full max-w-[180px]"
              priority
            />
          </Link>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          <p className="px-3 pb-2 pt-1 text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground">Navigation</p>

          {navItems.map(({ href, label, icon: Icon }) => {
            const active = pathname === href;
            return (
              <Link
                key={href}
                href={href}
                onClick={() => setOpen(false)}
                className={cn(
                  "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors",
                  active
                    ? "bg-sky-500/12 font-medium text-sky-500"
                    : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                )}
              >
                <Icon className={cn("size-4", active ? "text-sky-500" : "")} />
                <span>{label}</span>
              </Link>
            );
          })}

        </nav>

        <div className="border-t border-sidebar-border p-4">
          <div className="flex items-center gap-3 rounded-xl border border-border bg-background/70 p-3">
            <UserButton />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-card-foreground">Signed in</p>
              <p className="text-xs text-muted-foreground">Your records stay scoped to your account</p>
            </div>
            <ThemeToggle />
          </div>
        </div>
      </aside>
    </>
  );
}
